import {
  AlertTriangle,
  ArrowDown,
  ArrowUpDown,
  ArrowUp,
  BadgeCheck,
  CheckSquare,
  Dot,
  Square,
} from "lucide-react";
import React, { useContext, useState, useRef, useEffect, useLayoutEffect } from "react";
import { createPortal } from "react-dom";

function TooltipCell({ value, students, label }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const popupRef = useRef(null);
  const [position, setPosition] = useState(null);

  useEffect(() => {
    function handleClick(e) {
      if (!ref.current?.contains(e.target) && !popupRef.current?.contains(e.target)) setOpen(false);
    }
    if (open) document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, [open]);

  useLayoutEffect(() => {
    if (!open) return;
    function updatePosition() {
      if (!ref.current || !popupRef.current) return;
      const anchor = ref.current.getBoundingClientRect();
      const margin = 8;
      const gap = 6;
      const width = Math.min(320, window.innerWidth - margin * 2);
      const below = Math.max(0, window.innerHeight - anchor.bottom - gap - margin);
      const above = Math.max(0, anchor.top - gap - margin);
      const height = Math.min(popupRef.current.scrollHeight, window.innerHeight - margin * 2);
      const openAbove = below < height && above > below;
      const maxHeight = Math.max(1, openAbove ? above : below);
      const top = openAbove ? anchor.top - gap - Math.min(height, maxHeight) : anchor.bottom + gap;
      setPosition({
        width,
        maxHeight,
        top: Math.max(margin, Math.min(top, window.innerHeight - margin - Math.min(height, maxHeight))),
        left: Math.max(margin, Math.min(anchor.left, window.innerWidth - width - margin)),
      });
    }
    function handleKeyDown(event) {
      if (event.key === "Escape") {
        setOpen(false);
        ref.current?.querySelector("button")?.focus();
      }
    }
    updatePosition();
    window.addEventListener("resize", updatePosition);
    window.addEventListener("scroll", updatePosition, true);
    document.addEventListener("keydown", handleKeyDown);
    return () => {
      window.removeEventListener("resize", updatePosition);
      window.removeEventListener("scroll", updatePosition, true);
      document.removeEventListener("keydown", handleKeyDown);
    };
  }, [open, students]);

  if (!value || value === 0) return <span>{value}</span>;

  const isVacation = label === "Vacation";

  const today = new Date();
  today.setHours(0, 0, 0, 0);

  function parseDateTo(dateStr) {
    if (!dateStr) return null;
    const parts = dateStr.includes("/") ? dateStr.split("/") : null;
    if (parts) return new Date(parts[2], parts[0] - 1, parts[1]);
    return new Date(dateStr);
  }

  function getDaysUntilEnd(s) {
    const d = parseDateTo(s.date_to);
    if (!d) return Infinity;
    return Math.ceil((d - today) / (1000 * 60 * 60 * 24));
  }

  const sortedStudents = isVacation && students
    ? [...students].sort((a, b) => getDaysUntilEnd(a) - getDaysUntilEnd(b))
    : students;

  const bands = isVacation && students
    ? [
        { label: "0-30",  count: students.filter(s => { const d = getDaysUntilEnd(s); return d >= 0 && d <= 30; }).length },
        { label: "31-60", count: students.filter(s => { const d = getDaysUntilEnd(s); return d >= 31 && d <= 60; }).length },
        { label: "61-90", count: students.filter(s => { const d = getDaysUntilEnd(s); return d >= 61 && d <= 90; }).length },
        { label: "91-120",count: students.filter(s => { const d = getDaysUntilEnd(s); return d >= 91 && d <= 120; }).length },
      ]
    : null;

  return (
    <div className="relative inline-block" ref={ref}>
      <button
        type="button"
        aria-expanded={open}
        aria-haspopup="dialog"
        onClick={(e) => { e.stopPropagation(); setPosition(null); setOpen((o) => !o); }}
        className="underline decoration-dotted font-semibold text-sky-700 hover:text-sky-900 cursor-pointer"
      >
        {value}
      </button>
      {open && createPortal(
        <div
          ref={popupRef}
          role="dialog"
          aria-label={label + " students"}
          onClick={(event) => event.stopPropagation()}
          style={{ position: "fixed", zIndex: 1000, width: 320, ...position, visibility: position ? "visible" : "hidden", overflowY: "auto", whiteSpace: "normal" }}
          className="bg-white border border-gray-200 rounded-lg shadow-xl p-3 flex flex-col gap-2"
        >
          <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider border-b pb-1">{label}</p>
          {isVacation && bands && (
            <div className="flex flex-row gap-1 border-b pb-2 mb-1">
              {bands.map((b) => (
                <div key={b.label} className="flex flex-col items-center flex-1 bg-gray-50 rounded p-1">
                  <span className="text-[9px] font-bold text-gray-400 uppercase">{b.label}</span>
                  <span className="text-sm font-bold text-sky-700">{b.count}</span>
                </div>
              ))}
            </div>
          )}
          {sortedStudents && sortedStudents.length > 0 ? (
            sortedStudents.map((s, i) => {
              const daysLeft = isVacation ? getDaysUntilEnd(s) : null;
              let badgeColor = "bg-gray-100 text-gray-500";
              if (daysLeft !== null && daysLeft !== Infinity) {
                if (daysLeft <= 30) badgeColor = "bg-green-100 text-green-700";
                else if (daysLeft <= 60) badgeColor = "bg-yellow-100 text-yellow-700";
                else if (daysLeft <= 90) badgeColor = "bg-orange-100 text-orange-700";
                else if (daysLeft <= 120) badgeColor = "bg-red-100 text-red-700";
              }
              return (
                <div key={i} className="flex flex-col gap-0.5">
                  <div className="flex flex-row items-center justify-between gap-2">
                    <span className="text-xs font-semibold text-gray-800">{s.name} {s.last_name}</span>
                    {isVacation && daysLeft !== null && daysLeft !== Infinity && (
                      <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded ${badgeColor}`}>{daysLeft}d</span>
                    )}
                  </div>
                  <span className="text-[10px] text-gray-400">{s.date_from} → {s.date_to}</span>
                </div>
              );
            })
          ) : (
            <span className="text-xs text-gray-400 italic">No students</span>
          )}
        </div>,
        document.body
      )}
    </div>
  );
}
import Icon from "../Icon";

// import { Container } from './styles';

export default function Grid({
  children,
  Context = null,
  selection = null,
  handleEdit = true,
  horizontalScroll = false,
  minWidthClassName = "",
  summaryRow = null,
}) {
  let { setSelected, selected } = { setSelected: () => null, selected: [] };
  if (selection !== null) {
    setSelected = selection.setSelected;
    selected = selection.selected;
  }
  if (!Context) {
    return null;
  }
  const {
    gridHeader,
    gridData,
    page,
    orderBy,
    setOrderBy,
    opened,
    handleOpened,
  } = useContext(Context);
  if (!gridHeader || !gridData) {
    return null;
  }
  const tableWidthClass = horizontalScroll ? "min-w-full" : "w-full";
  const tableMinWidthClass =
    horizontalScroll && minWidthClassName ? minWidthClassName : "";
  const Table = () => (
    <table
      className={`relative bg-secondary-50 rounded-xl p-4 ${tableWidthClass} ${tableMinWidthClass} table-auto text-xs overflow-hidden text-left whitespace-nowrap`}
    >
      <thead className="sticky top-0 border-md">
        <tr className="bg-secondary h-8">
          {selection !== null && (
            <th className="px-4 w-12">
              <div className="flex flex-row justify-center items-center gap-2">
                Sel.
              </div>
            </th>
          )}
          {gridHeader.length > 0 &&
            gridHeader.map((head, index) => {
              return (
                <th className="px-4" key={index}>
                  <button
                    type="button"
                    onClick={() =>
                      setOrderBy({
                        column: head.name,
                        asc: orderBy?.column === head.name ? !orderBy.asc : true,
                        page,
                      })
                    }
                    className={`flex flex-row items-center justify-between w-full ${
                      orderBy && orderBy.column === head.name && "text-primary"
                    }`}
                  >
                    <div className={`flex flex-row items-center gap-2`}>
                      {head.title}{" "}
                      {gridHeader[index].action && (
                        <Icon name={gridHeader[index].action.icon} size={12} />
                      )}
                    </div>{" "}
                    {orderBy && orderBy.column === head.name && (
                      <>
                        {orderBy.asc ? (
                          <ArrowUp size={12} color="#0B2870" />
                        ) : (
                          <ArrowDown size={12} color="#0B2870" />
                        )}
                      </>
                    )}
                  </button>
                </th>
              );
            })}
        </tr>
      </thead>
      <tbody className="align-center">
        {summaryRow && (
          <tr className="h-10 border-b-2 border-orange-400 bg-orange-50 font-bold text-gray-800">
            {selection !== null && <td className="px-4" />}
            {gridHeader.map((_, index) => {
              const value = summaryRow.fields[index] ?? "";
              return (
                <td
                  key={index}
                  className={`px-4 whitespace-nowrap ${
                    typeof value === "string" && value.startsWith("($")
                      ? "text-red-700"
                      : typeof value === "string" && value.startsWith("$")
                      ? "text-green-700"
                      : ""
                  }`}
                >
                  {value}
                </td>
              );
            })}
          </tr>
        )}
        {gridData.filter((row) => row.show).length > 0 ? (
          gridData
            .filter((row) => row.show)
            .map((row, index) => {
              return (
                row.show && (
                  <tr
                    key={index}
                    className={`${
                      opened === row.id
                        ? "bg-mila_orange text-white"
                        : row.canceled
                        ? "opacity-40"
                        : "odd:bg-white"
                    } h-10  hover:rounded-sm hover:border hover:border-mila_orange cursor-pointer`}
                  >
                    {selection !== null && (
                      <>
                        {!row.selectable ? (
                          <td className="px-4">
                            <Dot size={18} />
                          </td>
                        ) : (
                          <td
                            className="px-4 pointer "
                            onClick={() =>
                              selection.multiple
                                ? selected.find(
                                    (selectedRow) => selectedRow.id === row.id
                                  )
                                  ? setSelected(
                                      selected.filter(
                                        (selectedRow) =>
                                          selectedRow.id !== row.id
                                      )
                                    )
                                  : setSelected([...selected, row])
                                : selected.find(
                                    (selectedRow) => selectedRow.id === row.id
                                  )
                                ? setSelected([])
                                : setSelected([row])
                            }
                          >
                            {selected.find(
                              (selectedRow) => selectedRow.id === row.id
                            ) ? (
                              <CheckSquare size={18} />
                            ) : (
                              <Square size={18} />
                            )}
                          </td>
                        )}
                      </>
                    )}
                    {row.fields.map((field, index) => {
                      if (gridHeader[index]?.type === "tooltip") {
                        const tooltipKey = gridHeader[index].tooltipKey;
                        const students = tooltipKey ? row.others?.[tooltipKey] : [];
                        return (
                          <td className="px-4" key={index} onClick={(e) => e.stopPropagation()}>
                            <TooltipCell
                              value={field}
                              students={students}
                              label={gridHeader[index].title}
                            />
                          </td>
                        );
                      }
                      if (gridHeader[index]?.type === "image") {
                        return (
                          <td
                            className="px-4 w-10"
                            key={index}
                            onClick={() =>
                              handleEdit ? handleOpened(row.id || null) : null
                            }
                          >
                            <img
                              src={field}
                              width="30"
                              className="rounded-md overflow-hidden"
                            />
                          </td>
                        );
                      }
                      if (gridHeader[index]?.type === "boolean") {
                        return (
                          <td
                            className={`px-4`}
                            key={index}
                            onClick={() =>
                              handleEdit ? handleOpened(row.id || null) : null
                            }
                          >
                            <div
                              className={`flex flex-row items-center justify-start gap-2`}
                            >
                              <span>{field ? "Yes" : "No"}</span>
                            </div>
                          </td>
                        );
                      }
                      if (gridHeader[index]?.type === "date") {
                        return (
                          <td
                            className={`px-4`}
                            key={index}
                            onClick={() =>
                              handleEdit ? handleOpened(row.id || null) : null
                            }
                          >
                            <div
                              className={`flex flex-row items-center justify-start gap-2`}
                            >
                              <span
                                className={`${
                                  row.attention && row.attention.show
                                    ? "text-red-500 flex flex-row items-center gap-1"
                                    : ""
                                }`}
                              >
                                {new Date(field).toLocaleDateString("en-US", {
                                  year: "numeric",
                                  month: "2-digit",
                                  day: "2-digit",
                                  timeZone: "UTC",
                                })}
                                {row.attention && row.attention.show && (
                                  <AlertTriangle size={12} />
                                )}
                              </span>
                            </div>
                          </td>
                        );
                      }
                      return (
                        <td
                          className={`px-4`}
                          key={index}
                          onClick={() =>
                            handleEdit ? handleOpened(row.id || null) : null
                          }
                        >
                          <div
                            className={`flex flex-row items-center justify-start gap-2`}
                          >
                            {typeof field === "string" &&
                            field?.includes("[badge]") ? (
                              <div className="flex flex-row items-center gap-1">
                                <span className="w-full py-1 px-2 bg-gray-900 text-white rounded-md">
                                  {field.replace("[badge]", "")}
                                </span>
                              </div>
                            ) : (
                              <span>{field}</span>
                            )}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                )
              );
            })
        ) : (
          <tr className={`bg-gray-50 text-gray-500 h-10`}>
            <td className="px-4 w-10" colSpan="100">
              There&apos;s nothing here yet.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );

  return (
    <div className="relative flex flex-1 items-start justify-start w-full h-screen overflow-y-scroll">
      {horizontalScroll ? (
        <div className="w-full overflow-x-auto">
          <Table />
        </div>
      ) : (
        <Table />
      )}
      {opened ? (
        <div
          className="fixed left-0 top-0 z-40 w-full h-full"
          style={{ background: "rgba(0,0,0,.2)" }}
        >
          {children}
        </div>
      ) : (
        children
      )}
    </div>
  );
}
